/**
 * One call in the simplified register, and the pill that says where it stands.
 *
 * READ FROM ACROSS A COUNTER, NOT FROM A CHAIR. The log's row is fourteen pixels of type in
 * six columns for somebody at a desk comparing calls; this is three columns at twenty-one
 * pixels for somebody glancing up from a job with their hands full. When, who, what they
 * wanted, and whether anybody still has to do something about it. Nothing else: no grade,
 * no money, no handoff, no play button - those are all one tap away in the pane beside it.
 *
 * WHAT THEY WANTED IS THE READING'S `ask`, ON UP TO TWO LINES. Not the agent's headline,
 * whose shape is whatever that agent's author sends - a job reference and an outcome word,
 * and the outcome is the pill's job - and not the summary, a sentence that a list this
 * narrow cut off after "The caller did not…". `ask` is a label under fifty characters,
 * which is two lines at this size, so the clamp is a guard rather than the usual case.
 *
 * TITLED BY THE NUMBER, AND BY THE HANDLE ONLY WHEN THERE IS NONE. The log still titles
 * every row by its handle, for the reason `handle()` gives: a demo call has no number, so a
 * log titled by numbers was a column of blanks. A register at a counter is the opposite
 * case - the phone number IS how the shop knows the customer, and it is the thing they are
 * about to ring. Never a name: a name is only ever what the caller said.
 *
 * A REAL LINK, for the reasons the log's row gives: the browser keeps focus on what was
 * pressed, and the back button walks through the calls somebody opened.
 */

import { Link } from "react-router-dom";
import type { CallSummary } from "../api";
import { handle, numberOf, phone, shortDay, timeOfDay } from "../format";
import { cx } from "../ui";
import { callStatus } from "./Handled";
import { Check } from "./Icons";

/** What a person reads a call by: its number, or its handle when it has none. */
export function titleOf(call: CallSummary): string {
  return phone(numberOf(call)) ?? handle(call.callId);
}

/**
 * Open or Handled, as a pill.
 *
 * WARM FOR OPEN AND GOOD FOR HANDLED, the two meaning tokens that already say "needs a
 * person" and "it worked" everywhere else in the product. The dot and the tick are the
 * second carrier the badge note in `styles.css` asks for: at a glance two pale pills are
 * the same object to a lot of readers, and the shape is what tells them apart.
 */
export function StatusPill({ call, className }: { call: CallSummary; className?: string }) {
  const status = callStatus(call);
  const open = status.kind === "open";
  return (
    <span
      className={cx(
        "inline-flex flex-none items-center gap-2 rounded-full px-3.5 py-1 text-lg font-semibold whitespace-nowrap",
        open ? "bg-warm-soft text-warm" : "bg-good-soft text-good",
        className,
      )}
    >
      {open ? (
        <span aria-hidden="true" className="size-2.5 rounded-full bg-current" />
      ) : (
        <Check size={17} aria-hidden="true" />
      )}
      {status.label}
    </span>
  );
}

export function SimpleRow({
  call,
  href,
  tz,
  selected,
}: {
  call: CallSummary;
  /** Built by the page from the current agent, like the log's `hrefFor`. */
  href: string;
  /** The business's clock. Undefined falls back to the reader's browser. */
  tz: string | null | undefined;
  selected?: boolean;
}) {
  const day = shortDay(call.startedAt, tz);

  return (
    <Link
      to={href}
      aria-current={selected ? "true" : undefined}
      className={cx(
        "grid min-h-22 grid-cols-[minmax(84px,118px)_minmax(0,1fr)_auto] items-center gap-x-4",
        /*
         * A NARROW LIST PUTS THE PILL UNDER THE WORDS rather than beside them. On a 1024px
         * tablet the list is a little over 300px wide, and three columns there left the
         * number and the question four letters each - "C…" - which is the one thing on the
         * row anybody reads. Measured against the LIST, not the window, for the reason the
         * dialog's panes are: the list's width is a share of the screen, not the screen.
         */
        "@max-[460px]:grid-cols-[84px_minmax(0,1fr)] @max-[460px]:gap-y-2",
        "rounded-lg px-4 py-3 transition-colors",
        selected ? "bg-accent-soft" : "hover:bg-sunken",
      )}
    >
      <span className="grid self-start text-xl leading-tight text-ink-soft">
        {day && <span>{day}</span>}
        <span className="nums">{timeOfDay(call.startedAt, tz)}</span>
      </span>

      <span className="grid min-w-0">
        {/* ONE TEXT NODE, for the same reason the log's title is: the tests read it whole. */}
        <span className="nums truncate text-xl font-bold text-ink">{titleOf(call)}</span>
        <span className="line-clamp-2 text-xl text-ink-soft">
          {call.analysis.ask ?? "No summary yet"}
        </span>
      </span>

      <StatusPill
        call={call}
        className="@max-[460px]:col-start-2 @max-[460px]:justify-self-start"
      />
    </Link>
  );
}
